import {
  get_conversation_action_endpoint,
  get_pending_conversation_action,
} from './floating_chat_widget_helpers.js';

export const floating_chat_action_methods = {
  open_context_menu(event, conversation, scope = 'header') {
    this.active_context_menu = {
      conversation,
      scope,
      x: Math.min(event.clientX, window.innerWidth - 190),
      y: Math.min(event.clientY, window.innerHeight - 180),
    };
  },

  async apply_conversation_action(action, conversation = this.active_context_menu?.conversation) {
    if (!conversation) return;
    const pending_action = get_pending_conversation_action(action);
    if (action === 'pin') conversation.is_pinned = !conversation.is_pinned;
    if (action === 'mute') conversation.is_muted = !conversation.is_muted;
    if (action === 'unread') await this.mark_conversation_read(conversation);
    if (pending_action) this.pending_conversation_action = { conversation, action: pending_action };
    this.active_context_menu = null;
  },

  async execute_conversation_action() {
    if (!this.pending_conversation_action || this.conversation_action_loading) return;
    const { conversation, action } = this.pending_conversation_action;
    this.conversation_action_loading = true;
    try {
      const body = await this.request(`/aic_one_api/one_chat_management/${get_conversation_action_endpoint(action)}/`, {
        method: 'POST',
        body: JSON.stringify({ conversation_id: conversation.id, scope: 'me' }),
      });
      if (body?.status !== 'success') throw new Error(body?.message || `Failed to ${action} chat`);
      this.apply_conversation_action_locally(conversation.id, action);
      this.pending_conversation_action = null;
      this.show_toast(action === 'clear' ? 'Chat cleared' : 'Chat deleted', this.get_conversation_name(conversation), 'success');
      window.dispatchEvent(new Event('refresh-unread-chats'));
    } catch (error) {
      this.show_toast('Action failed', error.message || 'Please try again.', 'error');
    } finally {
      this.conversation_action_loading = false;
    }
  },

  apply_conversation_action_locally(conversation_id, action) {
    if (action === 'delete') {
      if (!this.hidden_conversation_ids.includes(conversation_id)) this.hidden_conversation_ids.push(conversation_id);
      this.conversations = this.conversations.filter(conv => String(conv.id) !== String(conversation_id));
      this.clear_active_conversation_if_matches(conversation_id, true);
      return;
    }
    const conversation = this.conversations.find(conv => String(conv.id) === String(conversation_id));
    if (conversation) {
      this.conversations.splice(this.conversations.indexOf(conversation), 1, {
        ...conversation,
        last_message: null,
        unread_count: 0,
        cleared_for_me: true,
      });
    }
    this.clear_active_conversation_if_matches(conversation_id);
  },

  clear_active_conversation_if_matches(conversation_id, clear_selection = false) {
    if (String(this.active_conversation_id) !== String(conversation_id)) return;
    if (clear_selection) this.active_conversation_id = null;
    this.messages = [];
    this.pinned_messages = [];
  },

  my_reaction_for(message) {
    if (!Array.isArray(message?.reactions)) return null;
    const reaction = message.reactions.find(item => {
      if (typeof item === 'string') return item.startsWith('me:');
      return item.reacted_by_me;
    });
    if (!reaction) return null;
    return typeof reaction === 'string' ? reaction.replace(/^me:/, '') : reaction.emoji;
  },

  formatted_reactions(message) {
    if (!Array.isArray(message?.reactions) || !message.reactions.length) return '';
    return message.reactions
      .map(reaction => (typeof reaction === 'string' ? reaction.replace(/^me:/, '') : reaction.emoji))
      .filter(Boolean)
      .join(' ');
  },

  async react_to_message(message, emoji, event) {
    if (event) event.stopPropagation();
    if (!message || this.reacting_message_id === message.id) return;
    this.reacting_message_id = message.id;
    try {
      const body = await this.request('/aic_one_api/one_chat_management/toggle_reaction/', {
        method: 'POST',
        body: JSON.stringify(this.get_reaction_payload(message, emoji)),
      });
      this.apply_reaction_response(message.id, body);
    } catch (error) {
      this.show_toast('Reaction failed', error.message || 'Network error while reacting.', 'error');
    } finally {
      this.reacting_message_id = null;
    }
  },

  get_reaction_payload(message, emoji) {
    return ({
      message_id: message.id,
      emoji: this.my_reaction_for(message) === emoji ? '' : emoji,
    });
  },

  apply_reaction_response(message_id, body) {
    if (body?.status === 'success') this.patch_message(message_id, { reactions: body.data?.reactions || [] });
  },

  set_reply(message) {
    this.reply_to = message;
    this.active_message_menu_id = null;
    this.$nextTick(() => this.$refs.composer_input?.focus());
  },

  clear_reply() {
    this.reply_to = null;
  },

  toggle_message_menu(message, event) {
    if (String(this.active_message_menu_id) === String(message.id)) {
      this.active_message_menu_id = null;
      return;
    }
    const scroller_rect = this.$refs.message_scroller?.getBoundingClientRect();
    const button_rect = event?.currentTarget?.getBoundingClientRect();
    const space_below = scroller_rect && button_rect ? scroller_rect.bottom - button_rect.bottom : 220;
    const space_above = scroller_rect && button_rect ? button_rect.top - scroller_rect.top : 80;
    const direction = space_above < 76 ? 'inside' : (space_below < 210 ? 'up' : 'down');
    this.set_message_menu_direction(message.id, direction);
    this.active_message_menu_id = message.id;
  },

  get_message_menu_direction(message) {
    const key = String(message?.id || '');
    return this.message_menu_direction.find(item => item.key === key)?.direction || 'down';
  },

  set_message_menu_direction(message_id, direction) {
    const key = String(message_id || '');
    if (!key) return;
    this.message_menu_direction = [
      ...this.message_menu_direction.filter(item => item.key !== key),
      { key, direction },
    ];
  },

  async pin_message(message) {
    if (!message || this.pinning_in_progress) return;
    this.pinning_in_progress = true;
    const next_value = !message.is_pinned;
    try {
      const body = await this.request('/aic_one_api/one_chat_management/pin_message/', {
        method: 'POST',
        body: JSON.stringify({ message_id: message.id, pin: next_value }),
      });
      if (body?.status === 'success' || body?.id) {
        this.patch_message(message.id, { is_pinned: next_value });
        await this.load_pinned_messages(this.active_conversation_id);
        this.active_message_menu_id = null;
      }
    } catch (error) {
      this.show_toast('Pin failed', error.message || 'Network error while pinning.', 'error');
    } finally {
      this.pinning_in_progress = false;
    }
  },

  async load_pinned_messages(conversation_id) {
    if (!conversation_id) return;
    try {
      const body = await this.request('/aic_one_api/one_chat_management/get_pinned_messages/', {
        method: 'POST',
        body: JSON.stringify({ conversation_id }),
      });
      if (body?.status === 'success') this.pinned_messages = body.data || [];
    } catch {
      // Non-blocking.
    }
  },

  edit_message(message) {
    if (!this.is_my_message(message)) return;
    this.editing_message_id = message.id;
    this.edit_draft = message.content || '';
    this.active_message_menu_id = null;
    this.$nextTick(() => {
      const input = Array.isArray(this.$refs.edit_message_input) ? this.$refs.edit_message_input[0] : this.$refs.edit_message_input;
      input?.focus();
      input?.setSelectionRange?.(input.value.length, input.value.length);
    });
  },

  cancel_edit() {
    this.editing_message_id = null;
    this.edit_draft = '';
  },

  async submit_edit(message) {
    const content = this.edit_draft.trim();
    if (!message || !content || this.saving_edit) return;
    this.saving_edit = true;
    try {
      const body = await this.request('/aic_one_api/one_chat_management/edit_message/', {
        method: 'POST',
        body: JSON.stringify({ message_id: message.id, content }),
      });
      this.apply_edit_response(message.id, body);
    } catch (error) {
      this.show_toast('Edit failed', error.message || 'Network error while editing.', 'error');
    } finally {
      this.saving_edit = false;
    }
  },

  apply_edit_response(message_id, body) {
    if (body?.status !== 'success' && !body?.id) return;
    this.patch_message(message_id, body.data || body);
    this.cancel_edit();
    this.show_toast('Message updated', 'Your edit has been saved.', 'success');
  },

  confirm_delete_message(message, scope = 'me') {
    this.pending_delete_message = message;
    this.pending_delete_scope = scope;
    this.active_message_menu_id = null;
  },

  async execute_delete_message() {
    const message = this.pending_delete_message;
    if (!message || this.deleting_message) return;
    this.deleting_message = true;
    try {
      const body = await this.request('/aic_one_api/one_chat_management/delete_message/', {
        method: 'POST',
        body: JSON.stringify({
          message_id: message.id,
          delete_for_everyone: this.pending_delete_scope === 'everyone',
          scope: this.pending_delete_scope,
        }),
      });
      if (body?.status === 'success') {
        this.messages = this.messages.filter(item => String(item.id) !== String(message.id));
        this.pinned_messages = this.pinned_messages.filter(item => String(item.id) !== String(message.id));
        this.pending_delete_message = null;
        this.pending_delete_scope = 'me';
      }
    } catch (error) {
      this.show_toast('Delete failed', error.message || 'Network error while deleting.', 'error');
    } finally {
      this.deleting_message = false;
    }
  },
};
